import React, { useState, createContext, useContext } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}
interface ToastContextType {
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = `toast_${Date.now()}`;
    const newToast = {
      id,
      message,
      type
    };
    setToasts(prev => [...prev, newToast]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" data-id="element-582" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-600" data-id="element-583" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-600" data-id="element-584" />;
    }
  };
  const getStyles = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };
  return <ToastContext.Provider value={{
    showToast
  }} data-id="element-585">
      {children}

      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2" data-id="element-586">
        {toasts.map(toast => <div key={toast.id} className={`
              flex items-center space-x-3 px-4 py-3 rounded-lg border shadow-lg
              animate-in slide-in-from-bottom-5 duration-300
              ${getStyles(toast.type)}
            `} data-id="element-587">
            {getIcon(toast.type)}
            <span className="text-sm font-medium" data-id="element-588">{toast.message}</span>
            <button onClick={() => removeToast(toast.id)} className="ml-2 hover:opacity-70 transition-opacity" data-id="element-589">
              <X className="h-4 w-4" data-id="element-590" />
            </button>
          </div>)}
      </div>
    </ToastContext.Provider>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}